"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { handoffLinkTarget } from "@/components/handoff-link-target";
import type { LinkTarget } from "@/components/link";
import { useListingCounts } from "@/hooks/use-listing-counts";

import { getAlbumSearchQueryOptions } from "./album-search-query";
import { getArtistSearchQueryOptions } from "./artist-search-query";
import { getCompanySearchQueryOptions } from "./company-search-query";
import type { SearchFilters } from "./use-search-state";

export function useSearchMusicResults(
  filters: SearchFilters,
  enabled: boolean,
  artistEnabled: boolean,
) {
  const trimmedQuery = filters.query.trim();
  const albumQuery = useQuery({
    ...getAlbumSearchQueryOptions({
      query: filters.query,
      page: filters.page,
      genre: filters.genre,
      style: filters.style,
      type: filters.type,
      format: filters.format,
      country: filters.country,
      artistCountry: filters.origin,
      yearFrom: filters.yearFrom,
      yearTo: filters.yearTo,
      labels: filters.label,
      companies: filters.company,
      enabled,
    }),
    placeholderData: keepPreviousData,
  });
  const artistQuery = useQuery({
    ...getArtistSearchQueryOptions({
      query: filters.query,
      genre: filters.genre,
      style: filters.style,
      artistCountry: filters.origin,
      enabled: enabled && artistEnabled,
    }),
    placeholderData: keepPreviousData,
  });
  const companyQuery = useQuery({
    ...getCompanySearchQueryOptions({
      query: trimmedQuery,
      enabled: enabled && trimmedQuery.length >= 2,
    }),
    placeholderData: keepPreviousData,
  });
  const albums = albumQuery.data ? albumQuery.data.albums : [];
  const artists = artistQuery.data ? artistQuery.data.artists : [];
  const companyMatches = companyQuery.data ? companyQuery.data : [];
  const hasMore = albumQuery.data ? albumQuery.data.hasMore : false;
  const facets = albumQuery.data ? albumQuery.data.facets : null;
  const searchMeta = albumQuery.data?.searchMeta;
  const workIds = useMemo(() => albums.map((album) => album.id), [albums]);
  const { listingMap } = useListingCounts({ workIds, enabled });
  const buildAlbumLink = useCallback(
    (album: { id: string; matchedBy?: string; releaseId?: string }): LinkTarget =>
      handoffLinkTarget({
        to: "/work/$id",
        params: { id: album.id },
        search: {
          q: album.matchedBy?.startsWith("code") ? searchMeta?.query : undefined,
          format: filters.format,
          country: filters.country,
          label: filters.label.length ? filters.label : undefined,
          company: filters.company.length ? filters.company : undefined,
        },
      }),
    [filters.company, filters.country, filters.format, filters.label, searchMeta],
  );
  return {
    albumQuery,
    artistQuery,
    albums,
    artists,
    companyMatches,
    hasMore,
    facets,
    listingMap,
    buildAlbumLink,
    isFetching: albumQuery.isFetching || artistQuery.isFetching,
  };
}
